import { CSSProperties, useState, useEffect } from 'react'
import useScreen from 'Hooks/useScreen'
import useFacebookConnections from 'Hooks/useFacebookConnections'
import { styx, clsx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import ButtonSymphony from 'elements/ButtonSymphony'
import Banner from 'elements/Banner'
import ConnectionsCampaignView from 'components/CampaignViews/ConnectionsCampaignView'
import AssistantWorkspaceActions from './AssistantWorkspaceActions'

interface AssistantWorkspaceFacebookConnectProps {
    workspaceContent: {
        type: 'facebookConnect';
        data: {
            reason?: string;
            nextStep?: string;
            skipOption?: boolean;
        };
        title?: string;
        isLoading?: boolean;
    };
    handleSendMessage: (message: string, hiddenData?: any) => void;
    /** Optional callback to close the workspace directly */
    onClose?: () => void;
}

const AssistantWorkspaceFacebookConnect = ({
    workspaceContent,
    handleSendMessage,
    onClose,
}: AssistantWorkspaceFacebookConnectProps) => {
    const { mobileView } = useScreen()
    const { 
        loggedInFacebookUser,
        selectedFacebookAdAccount,
        selectedFacebookPage,
        selectedInstagramPage,
        selectedFacebookPixel,
        renderFacebookLoginButton,
        renderFacebookErrorMessage,
        getFacebookPixels,
        setFacebookPixel,
        isLoadingFacebookPixels
    } = useFacebookConnections()

    const { data, isLoading } = workspaceContent
    const { reason, nextStep, skipOption = true } = data

    const [availablePixels, setAvailablePixels] = useState<any[]>([])
    const [skipPixelSetup, setSkipPixelSetup] = useState(false)
    const [connectionStep, setConnectionStep] = useState<'login' | 'setup' | 'pixel' | 'complete'>('login')

    // Monitor connection status and update step accordingly
    useEffect(() => {
        let newStep: 'login' | 'setup' | 'pixel' | 'complete' = 'login'

        if (!loggedInFacebookUser) {
            newStep = 'setup'
        } else if (loggedInFacebookUser && (!selectedFacebookAdAccount || !selectedFacebookPage)) {
            newStep = 'setup'
        } else if (loggedInFacebookUser && selectedFacebookAdAccount && selectedFacebookPage) {
            // Keep in setup step - don't auto-transition to complete
            newStep = 'setup'
        }

        if (newStep !== connectionStep) {
            setConnectionStep(newStep)
        }
    }, [loggedInFacebookUser, selectedFacebookAdAccount, selectedFacebookPage, connectionStep])

    const loadPixels = async () => {
        if (selectedFacebookAdAccount) {
            try {
                const pixels = await getFacebookPixels()
                setAvailablePixels(pixels)
            } catch (error) {
                console.error('Error loading pixels:', error)
                setAvailablePixels([])
            }
        }
    }

    const handleSkipConnection = () => {
        // Send message to assistant about the choice
        handleSendMessage('I want to skip connecting Facebook', {
            fbSkipped: true,
            expressSetup: true,
            reason: 'User chose Express Setup over connecting their own Facebook account',
        })
        
        // Close workspace directly if callback is available
        if (onClose) {
            onClose()
        }
    }

    const handleSkipPixel = () => {
        setSkipPixelSetup(true)
        setConnectionStep('complete')
    }

    const handlePixelSelection = async (pixel: any) => {
        try {
            await setFacebookPixel(pixel)
            setConnectionStep('complete')
        } catch (error) {
            console.error('Error setting pixel:', error)
        }
    }

    const handleReadyToGo = () => {
        // Build the connection message
        const connectedItems = []
        
        if (loggedInFacebookUser) {
            connectedItems.push('Facebook Login')
        }
        if (selectedFacebookPage) {
            connectedItems.push(`Facebook Page (${selectedFacebookPage.name})`)
        }
        if (selectedInstagramPage?.instagramAccount?.username) {
            connectedItems.push(`Instagram (@${selectedInstagramPage.instagramAccount.username})`)
        }
        if (selectedFacebookAdAccount) {
            connectedItems.push(`Ad Account (${selectedFacebookAdAccount.name})`)
        }
        if (selectedFacebookPixel) {
            connectedItems.push(`Facebook Pixel (${selectedFacebookPixel.name})`)
        }

        const connectionsList = connectedItems.join(', ')
        const connectionMessage = `I connected my ${connectionsList}. We're ready to go!`
        
        handleSendMessage(connectionMessage)
        
        // Close workspace directly if callback is available
        if (onClose) {
            onClose()
        }
    }

    const renderConnectionBenefits = () => {
        return (
            <div className="connectionBenefitsSection" style={sty.benefitsSection}>
                <div className="benefitsList" style={sty.benefitsList}>
                    <div className="benefitItem" style={sty.benefitItem}>
                        <Icon className="benefitIcon" icon="users" size={16} />
                        <div className="benefitText" style={sty.benefitText}>
                            <strong>Better targeting</strong> with your audience insights
                        </div>
                    </div>
                    
                    <div className="benefitItem" style={sty.benefitItem}>
                        <Icon className="benefitIcon" icon="instagram" size={16} />
                        <div className="benefitText" style={sty.benefitText}>
                            <strong>Use your Instagram posts</strong> directly in ads
                        </div>
                    </div>
                    
                    <div className="benefitItem" style={sty.benefitItem}>
                        <Icon className="benefitIcon" icon="chart" size={16} />
                        <div className="benefitText" style={sty.benefitText}>
                            <strong>Track conversions</strong> and optimize performance
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const getActionsForStep = () => {
        // Check if minimum required connections are established
        const isReadyToGo = loggedInFacebookUser && selectedFacebookAdAccount && selectedFacebookPage
        
        if (isReadyToGo) {
            return (
                <AssistantWorkspaceActions
                    icon="checkCircle"
                    title="Facebook Account Connected Successfully!"
                    description="Your Facebook integration is complete and ready for campaign creation."
                    buttons={[
                        {
                            text: "We're ready to go →",
                            onClick: handleReadyToGo,
                            variant: 'primary',
                            icon: 'checkCircle'
                        }
                    ]}
                    sticky={true}
                />
            )
        }

        if (!skipOption) return null

        // Express setup as primary button, skip as secondary
        return (
            <AssistantWorkspaceActions
                icon="zap"
                title="Quick Start with Express Setup"
                description="Want to get started immediately? Use Symphony's managed ad accounts for instant campaign creation. You can always connect your own accounts later for better performance."
                buttons={[
                    {
                        text: 'Use Express Setup →',
                        onClick: handleSkipConnection,
                        variant: 'primary',
                        icon: 'zap'
                    },
                    {
                        text: 'Skip Facebook Connection',
                        onClick: handleSkipConnection,
                        variant: 'text'
                    }
                ]}
                sticky={true}
            />
        )
    }

    const renderSetupStep = () => {
        return (
            <div className="setupStepSection" style={sty.stepSection}>
                <div className="stepHeader" style={sty.stepHeader}>
                    <Icon className="stepIcon" icon="facebook" size={32} />
                    <div>
                        <div className="stepTitle" style={sty.stepTitle}>
                            Connect Your Facebook Account
                        </div>
                        <div className="stepSubtitle" style={sty.stepSubtitle}>
                            {reason || "Get the most out of your campaigns with Facebook integration"}
                        </div>
                    </div>
                </div>

                    <div className="setupContent" style={sty.setupContent}>
                        <ConnectionsCampaignView
                            className="connectionsSetup"
                            variant="facebook"
                            allowExpressSetup={false}
                            title=""
                            subtitle=""
                        />
                    </div>
            </div>
        )
    }

    const renderPixelStep = () => {
        return (
            <div className="pixelStepSection" style={sty.stepSection}>
                <div className="stepHeader" style={sty.stepHeader}>
                    <Icon className="stepIcon" icon="chart" size={32} />
                    <div>
                        <div className="stepTitle" style={sty.stepTitle}>
                            Connect Facebook Pixel (Optional)
                        </div>
                        <div className="stepSubtitle" style={sty.stepSubtitle}>
                            Add your Facebook Pixel for advanced conversion tracking and better campaign optimization
                        </div>
                    </div>
                </div>

                {renderConnectionBenefits()}

                <div className="connectionStepContent" style={sty.connectionStepContent}>
                    <div className="pixelSelection" style={sty.pixelSelection}>
                        {isLoadingFacebookPixels ? (
                            <div className="pixelLoading" style={sty.pixelLoading}>
                                <Icon className="loadingIcon" icon="refresh" size={24} />
                                <div>Loading your Facebook Pixels...</div>
                            </div>
                        ) : availablePixels.length > 0 ? (
                            <div className="pixelOptions" style={sty.pixelOptions}>
                                <div className="pixelOptionsTitle" style={sty.pixelOptionsTitle}>
                                    Choose a Facebook Pixel:
                                </div>
                                {availablePixels.map((pixel) => (
                                    <div
                                        key={pixel.id}
                                        className="pixelOption"
                                        style={sty.pixelOption}
                                        onClick={() => handlePixelSelection(pixel)}
                                    >
                                        <Icon className="pixelIcon" icon="chart" size={20} />
                                        <div className="pixelInfo">
                                            <div className="pixelName" style={sty.pixelName}>
                                                {pixel.name}
                                            </div>
                                            <div className="pixelId" style={sty.pixelId}>
                                                ID: {pixel.id}
                                            </div>
                                        </div>
                                        <Icon className="selectIcon" icon="arrowRight" size={16} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="noPixels" style={sty.noPixels}>
                                <Icon className="noPixelsIcon" icon="chart" size={48} />
                                <div className="noPixelsTitle" style={sty.noPixelsTitle}>
                                    No Facebook Pixels Found
                                </div>
                                <div className="noPixelsText" style={sty.noPixelsText}>
                                    You don't have any Facebook Pixels set up for this ad account. You can create one later in Facebook Business Manager.
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pixelActions" style={sty.pixelActions}>
                        <ButtonSymphony
                            className="skipPixelButton"
                            variant="text"
                            onClick={handleSkipPixel}
                        >
                            Skip Pixel Setup
                        </ButtonSymphony>
                    </div>
                </div>
            </div>
        )
    }

    const renderCompleteStep = () => {
        return (
            <div className="completeStepSection" style={sty.stepSection}>
                <div className="stepHeader" style={sty.stepHeader}>
                    <Icon className="stepIcon" icon="checkCircle" size={32} />
                    <div>
                        <div className="stepTitle" style={sty.stepTitle}>
                            Successfully Connected! 🎉
                        </div>
                        <div className="stepSubtitle" style={sty.stepSubtitle}>
                            Your Facebook account is ready for campaign creation
                        </div>
                    </div>
                </div>

                <Banner
                    className="successBanner"
                    color="green"
                    style={sty.successBanner}
                >
                    <div className="successContent" style={sty.successContent}>
                        <div className="successText">
                            <strong>Connected accounts:</strong>
                        </div>
                        <div className="connectedAccounts" style={sty.connectedAccounts}>
                            {selectedFacebookPage && (
                                <div>✓ Facebook Page: {selectedFacebookPage.name}</div>
                            )}
                            {selectedInstagramPage && (
                                <div>✓ Instagram: @{selectedInstagramPage.instagramAccount?.username}</div>
                            )}
                            {selectedFacebookAdAccount && (
                                <div>✓ Ad Account: {selectedFacebookAdAccount.name}</div>
                            )}
                            {selectedFacebookPixel && (
                                <div>✓ Facebook Pixel: {selectedFacebookPixel.name}</div>
                            )}
                        </div>
                    </div>
                </Banner>

                <div className="nextStepInfo" style={sty.nextStepInfo}>
                    {nextStep || "I'll now continue creating your campaign with these connected accounts."}
                </div>
            </div>
        )
    }

    const renderCurrentStep = () => {
        switch (connectionStep) {
            case 'login':
                return renderSetupStep()
            case 'setup':
                return renderSetupStep()
            case 'pixel':
                return renderPixelStep()
            case 'complete':
                return renderCompleteStep()
            default:
                return renderSetupStep()
        }
    }

    if (isLoading) {
        return (
            <div className="assistantWorkspaceFacebookConnectLoading" style={sty.loadingContainer}>
                <Icon 
                    className="loadingIcon"
                    icon="maestroBadgeImage" 
                    size={48} 
                    style={sty.loadingIcon}
                />
                <div style={sty.loadingText}>Setting up Facebook connection...</div>
            </div>
        )
    }

    return (
        <div
            className={clsx("assistantWorkspaceFacebookConnect", mobileView && "mobile")}
            style={styx(sty.container, mobileView && sty.container_mobile)}
        >
            {renderCurrentStep()}
            {getActionsForStep()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        padding: 0,
        width: '100%',
        minHeight: 400,
    },
    container_mobile: {
        gap: 16,
    },

    // Step Section
    stepSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        paddingBottom: 16,
        borderBottom: Sty.border,
    },
    stepTitle: {
        ...Sty.text20bold,
        color: Colors.textColor,
        marginBottom: 4,
    },
    stepSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
        lineHeight: 1.4,
    },

    // Benefits Section
    benefitsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    benefitsTitle: {
        ...Sty.text16bold,
        color: Colors.textColor,
    },
    benefitsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    benefitItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: 12,
        backgroundColor: 'white',
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
    },
    benefitText: {
        fontSize: 14,
        lineHeight: 1.4,
        color: Colors.textColor,
    },

    // Login Actions
    loginActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },

    // Setup Content
    setupContent: {
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 16,
        border: `1px solid ${Colors.grey}`,
    },

    // Pixel Section
    pixelSelection: {
        marginBottom: 20,
    },
    pixelLoading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: 40,
        fontSize: 14,
        color: Colors.greyDark,
    },
    pixelOptions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    pixelOptionsTitle: {
        ...Sty.text16bold,
        color: Colors.textColor,
        marginBottom: 8,
    },
    pixelOption: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    pixelName: {
        ...Sty.text14bold,
        color: Colors.textColor,
    },
    pixelId: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    noPixels: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: 40,
        textAlign: 'center',
    },
    noPixelsTitle: {
        ...Sty.text16bold,
        color: Colors.textColor,
    },
    noPixelsText: {
        fontSize: 14,
        color: Colors.greyDark,
        lineHeight: 1.4,
        maxWidth: 300,
    },
    pixelActions: {
        display: 'flex',
        justifyContent: 'center',
        borderTop: Sty.border,
        paddingTop: 16,
    },

    // Success Section
    successBanner: {
        margin: 0,
    },
    successContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    connectedAccounts: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 14,
        marginLeft: 8,
    },
    nextStepInfo: {
        padding: 16,
        backgroundColor: Colors.purpleLightest,
        borderRadius: 8,
        border: `1px solid ${Colors.purpleLighter}`,
        fontSize: 14,
        color: Colors.textColor,
        textAlign: 'center',
        fontStyle: 'italic',
    },

    // Loading State
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 16,
    },
    loadingIcon: {
        color: Colors.purple,
        animation: 'spin 2s linear infinite',
    },
    loadingText: {
        fontSize: 16,
        color: Colors.greyDark,
        textAlign: 'center',
    },



    // Connection Step Content
    connectionStepContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
}

export default AssistantWorkspaceFacebookConnect 